import { type SupplierListItem } from '@choco-admin/types';

import { type GetSupplierDetailsDataQuery } from '../../../generated';
import { openRing } from '../utils/deliveryZones';
import { isValidSortCartBy, toPricingDateSource } from '../utils/typeGuards';

export const mapSupplierToProfileItem = (
  supplier: NonNullable<GetSupplierDetailsDataQuery['supplier']>
): SupplierListItem => {
  return {
    id: supplier.id,
    name: supplier.name || null,
    description: supplier.description || null,
    primaryAssortment: supplier.primaryAssortment || null,
    secondaryAssortments: supplier.secondaryAssortments || null,
    deliveryCosts: supplier.deliveryCosts || null,
    minOrder: supplier.minOrder
      ? {
          price: supplier.minOrder.price
            ? {
                currency: supplier.minOrder.price.currency ?? null,
                amount: supplier.minOrder.price.amount ?? null,
              }
            : null,
          strict: supplier.minOrder.strict ?? null,
        }
      : null,
    referenceId: supplier.referenceId || null,
    accessPackage: supplier.accessPackage || null,
    isIntegrated: supplier.isIntegrated || null,
    locale: supplier.locale || null,
    timezone: supplier.timezone || null,

    isTest: supplier.isTest ?? null,
    isPuppy: supplier.isPuppy ?? null,
    // Two things happen here. Apollo's __typename is dropped, because these
    // objects go straight back as DeliveryZoneInput on save and AppSync rejects
    // unknown input fields. And the repeated closing corner is removed, so the
    // editor shows the corners a person placed (see openRing).
    deliveryZones:
      supplier.deliveryZones?.map((zone) => ({
        name: zone.name,
        points: openRing(
          zone.points.map((point) => ({
            latitude: point.latitude,
            longitude: point.longitude,
          }))
        ),
      })) ?? null,
    contactEmails: supplier.contactEmails || null,
    botUserId: supplier.botUser?.id || null,
    botUser: supplier.botUser
      ? {
          profileImage: supplier.botUser.profileImage
            ? {
                uploadUrl: supplier.botUser.profileImage.uploadUrl || null,
                smallSquare: supplier.botUser.profileImage.smallSquare || null,
                mediumSquare:
                  supplier.botUser.profileImage.mediumSquare || null,
                largeSquare: supplier.botUser.profileImage.largeSquare || null,
              }
            : null,
        }
      : null,
    coverPhoto: supplier.coverPhoto
      ? {
          url: supplier.coverPhoto.url || null,
          uploadUrl: supplier.coverPhoto.uploadUrl || null,
          smallRectangle: supplier.coverPhoto.smallRectangle || null,
          mediumRectangle: supplier.coverPhoto.mediumRectangle || null,
          largeRectangle: supplier.coverPhoto.largeRectangle || null,
        }
      : null,
    allowCustomProducts: supplier.allowCustomProducts || null,
    customerNumberMandatory: supplier.customerNumberMandatory || null,
    deliveryDateMandatory: supplier.deliveryDateMandatory || null,
    allowBuyerComment: supplier.allowBuyerComment || null,
    enablePromotions: supplier.enablePromotions ?? null,
    enableTaxes: supplier.enableTaxes ?? null,
    pricingDateSource: toPricingDateSource(supplier.pricingDateSource),
    calculationPrecision: supplier.calculationPrecision ?? null,
    hasEnabledFractionalUnits: supplier.hasEnabledFractionalUnits || null,
    allowOnlyOneActiveVariant: supplier.allowOnlyOneActiveVariant || null,
    allowInvoiceOrderGuidePhotoUpload:
      supplier.allowInvoiceOrderGuidePhotoUpload ?? null,
    disableCustomerNumberEdits: supplier.disableCustomerNumberEdits ?? null,
    allowOrderedProductMerging: supplier.allowOrderedProductMerging ?? null,
    enableEcommerceProductGridView:
      supplier.enableEcommerceProductGridView ?? null,
    allowEcommercePoNumber: supplier.allowEcommercePoNumber ?? null,
    allowCustomerAcquisition: supplier.allowCustomerAcquisition ?? null,
    sortCartBy: isValidSortCartBy(supplier.sortCartBy)
      ? supplier.sortCartBy
      : null,
    // Catalog settings
    noDefaultCatalog: supplier.noDefaultCatalog || false,
    defaultSubCatalog: supplier.defaultSubCatalog || null,
    priceSetting: supplier.priceSetting
      ? {
          displayInMarketplace: supplier.priceSetting.displayInMarketplace,
          displayToCustomers: supplier.priceSetting.displayToCustomers,
        }
      : null,
    hideMyPrice: supplier.hideMyPrice || false,

    // Notification/integration settings — preserve the full array so the form
    // can seed its fields and later merge unexposed entries back on save.
    integration: supplier.integration.length > 0 ? supplier.integration : null,
  };
};
